/* eslint-disable no-restricted-syntax -- container width/height are prop-driven (boxSize) */
import type { ColorType } from "@UI/types/colors.ts";
import type { IconNameType } from "@UI/types/icons.ts";
import type { PixelValueType } from "@UI/types/layout.ts";
import type { FC, ReactNode } from "react";
import type { PlacesType } from "react-tooltip";
import {
  TOOLTIP_DARK_THEME_CLASSES,
  TOOLTIP_DELAY_SHOW,
} from "@UI/defaults/tooltip.ts";
import classNames from "classnames";
import { Tooltip } from "react-tooltip";
import { SPRITE_VERSION } from "@UI/defaults/spriteVersion.ts";
import {
  GRADIENT_ICONS,
  type GradientIconName,
} from "@UI/defaults/gradientIcons.ts";

export interface Props {
  className?: string;
  name: IconNameType;
  color?: ColorType;
  height?: PixelValueType;
  width?: PixelValueType;
  boxSize?: PixelValueType;
  dataTooltipId?: string;
  tooltip?: string | ReactNode;
  tooltipPlace?: PlacesType;
}

const isGradientIcon = (
  color: ColorType,
  name: IconNameType,
): name is GradientIconName =>
  color === "ai" && (GRADIENT_ICONS as readonly string[]).includes(name);

const DIcon: FC<Props> = ({
  className,
  name,
  dataTooltipId,
  tooltipPlace,
  tooltip,
  color = "base",
  height = "16px",
  width = "16px",
  boxSize,
}) => {
  const colorClass = color === "inherit" ? "d-icon-inherit" : `d-icon-${color}`;

  const svgClasses = classNames("d-flex-shrink-0", colorClass);

  // "ai" is the one colour a <use> cannot take: the sprite's paths hardcode
  // stroke="currentColor", so no gradient can reach them. Those icons are
  // emitted as CSS masks (`iconMasks.scss`) and painted as a gradient instead.
  const svg = isGradientIcon(color, name) ? (
    <span
      className={classNames("d-icon-ai d-flex-shrink-0", `d-icon-ai--${name}`)}
      style={{ width, height }}
    />
  ) : (
    <svg className={svgClasses} height={height} width={width}>
      <use href={`/sprite.svg?v=${SPRITE_VERSION}#${name}`} />
    </svg>
  );

  const tooltipElement = tooltip && dataTooltipId && (
    <Tooltip
      className={TOOLTIP_DARK_THEME_CLASSES}
      id={dataTooltipId}
      opacity={1}
      place={tooltipPlace}
      delayShow={TOOLTIP_DELAY_SHOW}
    >
      {tooltip}
    </Tooltip>
  );

  return (
    <>
      <div
        className={classNames(
          "d-icon-box d-flex d-align-center d-justify-center d-flex-shrink-0",
          className,
        )}
        style={boxSize ? { width: boxSize, height: boxSize } : undefined}
        data-tooltip-id={dataTooltipId}
      >
        {svg}
      </div>
      {tooltipElement}
    </>
  );
};

export default DIcon;
